import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { translations, type Language, type Translations } from './translations';

interface LanguageContextType {
  language: Language;
  t: Translations;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

const TAGLINE_ROTATION_INTERVAL = 8000; // 8 seconds
const FADE_DURATION = 500; // 0.5 seconds

export function useRotatingTagline() {
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    intervalRef.current = window.setInterval(() => {
      // Start fade out
      setIsFading(true);

      // After fade out completes, change text and fade in
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % t.taglines.length);
        setIsFading(false);
      }, FADE_DURATION);
    }, TAGLINE_ROTATION_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [t.taglines.length]);

  return { tagline: t.taglines[index], isFading };
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = 'polaris_language';

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'tr') {
      return stored;
    }
    return 'tr'; // Default to Turkish
  });

  const t = translations[language];

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState((prev) => (prev === 'tr' ? 'en' : 'tr'));
  }, []);

  return (
    <LanguageContext.Provider value={{ language, t, setLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
